// Wire protocol between the widget and a conversation, over one WebSocket.

export type Role = "visitor" | "assistant" | "owner" | "system";

export interface ChatMessage {
  id: string;
  role: Role;
  text: string;
  at: number;
  /** Display name for owner messages ("Chris"). */
  author?: string;
  /** Tools the assistant ran for this reply, so it remembers them on later turns. Not shown. */
  tools?: ToolUse[];
  /** The page a visitor message was sent from, so the assistant can tell when they moved on. Not shown. */
  page?: { url: string; title: string };
}

export interface ToolUse {
  name: string;
  input: unknown;
}

/** Who is answering the visitor right now. */
export type Mode = "ai" | "human";

/**
 * Something the page lets the agent do in the visitor's browser, like
 * switching to dark mode. Pages register these on window.conversationalAgentActions.
 */
export interface PageAction {
  name: string;
  description: string;
  /** JSON Schema for the arguments. */
  parameters?: Record<string, unknown>;
}

export interface PageContext {
  url: string;
  title: string;
  description?: string;
  /** Visible text of the page, trimmed by the widget. */
  text?: string;
  actions?: PageAction[];
}

export type ClientEvent =
  | { type: "hello"; page?: PageContext }
  /** id is chosen by the widget, so it can show the message before the server echoes it. */
  | { type: "message"; text: string; page?: PageContext; id?: string };

export type ServerEvent =
  | { type: "history"; messages: ChatMessage[]; mode: Mode; ownerName: string; greeting?: string }
  | { type: "message"; message: ChatMessage }
  /** Streaming text for an assistant message that is still being written. */
  | { type: "delta"; id: string; text: string }
  | { type: "typing"; role: "assistant" | "owner" }
  | { type: "mode"; mode: Mode; ownerName: string }
  /** Run one of the page's actions in the visitor's browser. */
  | { type: "action"; name: string; args: Record<string, unknown> }
  | { type: "error"; message: string };

/** The Turnstile action the widget renders with and the Worker requires. */
export const TURNSTILE_ACTION = "start_chat";
